<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BadgeCampaignList" 
          :extra-items="['徽章管理']" 
        />
      </div>
    </div>
    <b-card>
      <b-overlay :show="showLoading" rounded="sm">
        <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
          <h4 class="col-12 col-xl-2 mb-2 mb-xl-0 font-weight-bold">徽章管理</h4>
          <div
            class="
              col-12 col-xl-8
              d-flex
              flex-column flex-xl-row
              align-items-start align-items-xl-center
            "
          >
          <b-button
            v-if="checkPermission([consts.BADGE_CREATE]) && badgeCampaignId"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            :to="{ name: 'BadgeCreateByBadgeCampaign' }"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>新增徽章
          </b-button>
          <b-form-select
            v-model="merchantId"
            :options="merchants"
            @change="handlePageResetWithoutReload"
            :disabled="merchants.length == 2"
          ></b-form-select>
          <b-form-select
            v-model="badgeCampaignId"
            :options="badgeCampaigns"
            @change="handlePageResetWithoutReload"
            :disabled="badgeCampaigns.length == 2"
          ></b-form-select>
          <b-input-group class="ml-0 ml-xl-4">
            <b-form-input 
              v-model="query.keyword" 
              placeholder="搜尋徽章名稱" 
              v-on:keyup.enter="handlePageResetWithoutReload"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
        <b-table
          responsive
          striped
          hover
          :items="badges"
          :fields="fields"
          @sort-changed="handleSort"
        >
          <template #cell(image_url)="data">
            <b-img-lazy
              v-if="data.item.image_url"
              class="thumb-image"
              :src="data.item.image_url"
              alt="徽章圖片"
              width="50"
              height="50"
            />
          </template>
          <template #cell(started_at_and_end_at)="data">
            <div class="mb-1">
              {{ formatDate(data.item.available_start_at) }}
            </div>
            <div>{{ formatDate(data.item.available_end_at) }}</div>
          </template>
          <template #cell(branch)="data">
            <div class="mb-1">
              {{ data.item.branch ? data.item.branch.name : "" }}
            </div>
            <div>
              {{ data.item.branch ? data.item.branch.branch_code : "" }}
            </div>
          </template>
          <template #cell(arrange)="data">
            <b-button
              class="ml-2"
              variant="inverse-primary"
              v-if="checkPermission([consts.BADGE_LIST_VIEW])"
              :to="{
                name: (merchantId && badgeCampaignId) ? 'BadgeViewByBadgeCampaign' : 'BadgeDetail',
                params: {
                  badgeId: data.item.id,
                  merchantId: merchantId,
                  badgeCampaignId: badgeCampaignId,
                },
              }"
            >
              <span class="mdi mdi-eye"></span> 查看
            </b-button>
            <b-button
              class="ml-2"
              variant="inverse-warning"
              v-if="checkPermission([consts.BADGE_EDIT])"
              :to="{
                name: (merchantId && badgeCampaignId) ? 'BadgeEditByBadgeCampaign' : 'BadgeEdit',
                params: {
                  badgeId: data.item.id,
                  merchantId: merchantId,
                  badgeCampaignId: badgeCampaignId,
                },
              }"
            >
              <span class="mdi mdi-lead-pencil"></span> 編輯
            </b-button>
            <b-button
              class="ml-2"
              variant="inverse-danger"
              @click="deleteBadge(data.item)"
            >
              <span class="mdi mdi-delete"></span> 刪除
            </b-button>
            <b-button
              class="ml-2"
              variant="inverse-primary"
              v-if="checkPermission([consts.BADGE_EDIT])"
              :to="{
                name: (merchantId && badgeCampaignId) ? 'BadgeCustomerListByCampaign' : 'BadgeCustomerList',
                params: {
                  badgeId: data.item.id,
                  merchantId: merchantId,
                  badgeCampaignId: badgeCampaignId,
                },
              }"
              >會員列表
            </b-button>
          </template>
        </b-table>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import badgeApi from "@/apis/badge";
import moment from "moment";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import badgeCampaignApi from "@/apis/badgeCampaign";
import merchantApi from "@/apis/merchant";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  mixins: [
    paginationMixin, 
  ],
  data() {
    return {
      consts,
      showLoading: false,
      currentPage: 1,
      totalRows: 0,
      perPage: 15,
      fields: [
        {
          key: "name",
          label: "徽章名稱",
        },
        {
          key: "image_url",
          label: "徽章圖片",
        },
        {
          key: "description",
          label: "徽章描述",
        },
        {
          key: "sort",
          label: "排序",
          sortable: true,
        },
        {
          key: "is_enabled",
          label: "狀態",
          formatter: (value) => {
            return value ? "啟用" : "停用";
          },
        },
        {
          key: "arrange",
          label: "管理",
        },
      ],
      badges: [],
      badgeCampaignId: null,
      nowBadgeCampaign: null,
      badgeCampaigns: [{ value: null, text: "請選擇徽章活動" }],
      merchantId: null,
      merchants: [{ value: null, text: "請選擇通路" }],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        sort_by: "sort",
        order_by: "asc",
        keyword: null,
      },
    };
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        this.updateUrl(this.query);
        this.fetchBadges();
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.fetchBadges();
    },
    merchantId(newMerchantId, oldMerchantId) {
      if (!this.initialized || newMerchantId === oldMerchantId) return;
      this.updateUrl(this.query);
      this.fetchBadgeCampaigns();
      this.fetchBadges();
    },
    badgeCampaignId(newBadgeCampaignId, oldBadgeCampaignId) {
      if (!this.initialized || newBadgeCampaignId === oldBadgeCampaignId) return;
      this.updateUrl(this.query);

      this.nowBadgeCampaign = this.badgeCampaigns.find((item) => {
        if (item.value === this.badgeCampaignId) {
          return item;
        }
      });

      this.fetchBadges();
    },
  },
  computed: {
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    }
  },
  async created() {
    this.badgeCampaignId = this.$route.params.badgeCampaignId || this.badgeCampaignId;
    this.merchantId = this.$route.params.merchantId || this.merchantId;
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {

    await this.fetchMerchants()
    await this.fetchBadgeCampaigns()
    await this.fetchBadges()
    
    this.currentPage = this.query.page;
    this.initialized = true
  },
  methods: {
    async fetchMerchants() {
      if (this.merchants.length > 1) return;

      const { data } = await merchantApi.list();
      this.merchants = [
        { value: null, text: "請選擇通路" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      if (this.merchantId) {
        var hasMerchant = this.merchants.find((item) => {
          return item.value === this.merchantId;
        });

        if (!hasMerchant) {
          this.$swal.fire({
            type: "warning",
            title: "查無此通路",
            confirmButtonText: "確定",
            confirmButtonColor: "#d33",
            reverseButtons: true,
          });
          return;
        }
      }
    },

    async fetchBadgeCampaigns() {
      if (!this.merchantId) {
        this.showLoading = false;
        return;
      }

      const params = {};
      const { data } = await badgeCampaignApi.getBadgeCampaigns(this.merchantId, params);

      this.badgeCampaigns = [
        { value: null, text: "請選擇徽章活動" },
        ...data.data.map((campaign) => {
          return {
            value: campaign.id,
            text: campaign.name,
          };
        }),
      ];

      if (this.badgeCampaignId) {
        this.nowBadgeCampaign = this.badgeCampaigns.find((item) => {
          if (item.value === this.badgeCampaignId) {
            return item;
          }
        });

        if (!this.nowBadgeCampaign) {
          this.badgeCampaignId = null;
          return;
        }
      }
    },

    async fetchBadges() {
      this.showLoading = true;

      try {
        const params = {
          ...this.query,
          badgeCampaignId: this.badgeCampaignId ? this.badgeCampaignId : null,
          merchantId: this.merchantId ? this.merchantId : null,
        };

        const { data } = await badgeApi.getBadges(params);

        this.badges = data.data;
        this.totalRows = data.meta.total;
        this.perPage = data.meta.per_page;
      } catch (error) {
        console.error(error);
        alert("取得徽章資料錯誤");
      }
      this.showLoading = false;
    },

    formatDate(value) {
      if (value) {
        return moment(String(value)).format("YYYY-MM-DD HH:mm");
      }
    },
    checkPermission (permissions) {
      const checker = new PermissionChecker;
      return checker.check(permissions);
    },
    async deleteBadge(badge) {
      await this.$swal
        .fire({
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          type: "warning",
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            let response = await badgeApi.removeBadge(badge.id);
            if (response.status === 200) {
              this.$swal.fire({
                title: "成功",
                type: "success",
                text: "成功刪除 ",
              });
              this.fetchBadges();
            } else {
              if (response.data.message) {
                this.$swal.fire({
                  title: "錯誤",
                  type: "error",
                  text: response.data.message,
                });
              }
            }
          }
        });
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageResetWithoutReload();
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
    },

    async updateUrl(query) {
      // filter out null value
      const filteredQuery = Object.keys(query).reduce((acc, key) => {
        if (query[key] !== null && 
            query[key] !== undefined && 
            query[key] !== "" && 
            !(key === 'page' && query[key] === 1)
        ) {
          acc[key] = query[key];
        }
        return acc;
      }, {});

      const currentRoute = this.$router.currentRoute;
      let needsNavigation = currentRoute.name !== "BadgeListByBadgeCampaign" ||
        JSON.stringify(currentRoute.query) !== JSON.stringify(filteredQuery) ||
        (currentRoute.params.badgeCampaignId !== this.badgeCampaignId) ||
        (currentRoute.params.merchantId !== this.merchantId);

      if (needsNavigation && this.badgeCampaignId && this.merchantId) {
        await this.$router.push({ 
          name: "BadgeListByBadgeCampaign", 
          query: filteredQuery,
          params: { merchantId: this.merchantId, badgeCampaignId: this.badgeCampaignId }
        });
      }
    },
  },
};
</script>

<style>
.modal-backdrop {
  opacity: 0.5;
}
</style>
